type AttributeType = 'benthic_attributes' | 'fish_species' | 'invert_species'

type AttributeCounts = Record<AttributeType, Record<string, number>>

interface CollectRecordObservationData {
  obs_belt_fishes?: { fish_attribute?: string | null }[]
  obs_belt_inverts?: { invert_attribute?: string | null }[]
  obs_benthic_lits?: { attribute?: string | null }[]
  obs_benthic_pits?: { attribute?: string | null }[]
  obs_colonies_bleached?: { attribute?: string | null }[]
  obs_quadrat_benthic_percent?: { attribute?: string | null }[]
  obs_benthic_photo_quadrats?: { attribute?: string | null }[]
}

interface CollectRecord {
  id?: string | number
  data?: CollectRecordObservationData
}

interface DexiePerUserDataInstance {
  collect_records: {
    toArray: () => Promise<CollectRecord[]>
  }
}

// Looks at all fish species and benthic attributes used in collect records,
// grouped by attribute type with counts per attribute ID.
const getAttributesInUse = async (
  dexiePerUserDataInstance: DexiePerUserDataInstance,
  pulledCollectRecords: CollectRecord[] = [],
): Promise<AttributeCounts> => {
  const attributeCounts: AttributeCounts = {
    benthic_attributes: {},
    fish_species: {},
    invert_species: {},
  }

  const incrementIfTruthy = (
    attributeId: string | null | undefined,
    attributeType: AttributeType,
  ) => {
    if (attributeId) {
      attributeCounts[attributeType][attributeId] =
        (attributeCounts[attributeType][attributeId] ?? 0) + 1
    }
  }

  const collectRecordsInUse = await dexiePerUserDataInstance.collect_records.toArray()
  const localRecordIds = new Set(
    collectRecordsInUse
      .map((record) => record?.id)
      .filter(
        (recordId): recordId is string | number => recordId !== undefined && recordId !== null,
      ),
  )

  // Local records are the source of truth when IDs overlap.
  // Pulled records without IDs are ignored because they cannot be deduplicated safely.
  const pulledRecordsNotInLocal = pulledCollectRecords.filter(
    (record) => record?.id !== undefined && record?.id !== null && !localRecordIds.has(record.id),
  )

  const collectRecordsToCount = [...collectRecordsInUse, ...pulledRecordsNotInLocal]

  collectRecordsToCount.forEach((record) => {
    const data = record?.data
    if (!data) {
      return
    }

    data.obs_belt_fishes?.forEach((obs) => incrementIfTruthy(obs.fish_attribute, 'fish_species'))
    data.obs_belt_inverts?.forEach((obs) =>
      incrementIfTruthy(obs.invert_attribute, 'invert_species'),
    )
    data.obs_benthic_lits?.forEach((obs) => incrementIfTruthy(obs.attribute, 'benthic_attributes'))
    data.obs_benthic_pits?.forEach((obs) => incrementIfTruthy(obs.attribute, 'benthic_attributes'))
    data.obs_colonies_bleached?.forEach((obs) =>
      incrementIfTruthy(obs.attribute, 'benthic_attributes'),
    )
    data.obs_quadrat_benthic_percent?.forEach((obs) =>
      incrementIfTruthy(obs.attribute, 'benthic_attributes'),
    )
    data.obs_benthic_photo_quadrats?.forEach((obs) =>
      incrementIfTruthy(obs.attribute, 'benthic_attributes'),
    )
  })

  return attributeCounts
}

export default getAttributesInUse
